import { useCallback, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { ordersService, type OrderView, type RestoreTableRequest } from '../../../services/orders/orders.service';
import { toHebrewApiErrorMessage } from '../../../services/index/index.service';
import type { PosTab } from '../core/pos-shell-page.types';

type Params = {
  tenantId: string;
  employeeSession: string | null;
  isManager: boolean;
  setActiveTab: (tab: PosTab) => void;
};

// Orchestrates the table-history + שחזר שולחן flow (state + handlers) so PosShellPage stays thin.
export const useTableHistoryRestore = ({ tenantId, employeeSession, isManager, setActiveTab }: Params) => {
  const [tableHistoryTarget, setTableHistoryTarget] = useState<{ tableId: number; tableLabel: string } | null>(null);
  // The closed order opened as a bill copy (null = show the history list).
  const [restoreSourceOrder, setRestoreSourceOrder] = useState<OrderView | null>(null);
  // Pending restore request awaiting an available-table pick on the floor map.
  const [restoreRequest, setRestoreRequest] = useState<RestoreTableRequest | null>(null);
  const restoreInFlightRef = useRef(false);

  const handleTableHistoryLongPress = useCallback((tableId: number, tableLabel: string) => {
    setTableHistoryTarget({ tableId, tableLabel });
    setRestoreSourceOrder(null); // Always land on the history list, not a previously-open restore bill.
    setActiveTab('table-history');
  }, [setActiveTab]);

  // Only managers open a history order's bill (restore); employees just browse the list.
  const handleSelectHistoryOrder = useCallback((order: OrderView) => {
    if (!isManager) return;
    setRestoreSourceOrder(order);
  }, [isManager]);

  const closeRestoreBill = useCallback(() => setRestoreSourceOrder(null), []);

  // Past-receipts routes a voided order here: fetch the full order, then open the restore bill.
  const openRestoreBillForOrderId = useCallback(async (orderId: number) => {
    if (!employeeSession) return;
    try {
      const order = await ordersService.getById(tenantId, orderId, employeeSession);
      setTableHistoryTarget({ tableId: order.tableId ?? 0, tableLabel: order.tableNumber ?? order.orderCode });
      setRestoreSourceOrder(order);
      setActiveTab('table-history');
    } catch (loadError) {
      notifications.show({
        color: 'red',
        title: 'שגיאה',
        message: toHebrewApiErrorMessage(loadError instanceof Error ? loadError.message : 'טעינת ההזמנה נכשלה'),
        autoClose: 3000,
      });
    }
  }, [tenantId, employeeSession, setActiveTab]);

  const handleRestoreRequest = useCallback((request: RestoreTableRequest) => {
    setRestoreRequest(request);
    setActiveTab('areas');
  }, [setActiveTab]);

  const handleCancelRestorePick = useCallback(() => {
    setRestoreRequest(null);
    setActiveTab('table-history');
  }, [setActiveTab]);

  const handlePickRestoreTable = useCallback(async (targetTableId: number) => {
    if (!restoreRequest || !employeeSession || restoreInFlightRef.current) {
      return;
    }
    restoreInFlightRef.current = true;
    try {
      await ordersService.restoreTable(tenantId, targetTableId, restoreRequest, employeeSession);
      // Success: SSE order.opened seeds the new order on the target table; leave restore flow.
      setRestoreRequest(null);
      setRestoreSourceOrder(null);
      setTableHistoryTarget(null);
      setActiveTab('areas');
    } catch (restoreError) {
      notifications.show({
        color: 'red',
        title: 'שגיאה',
        message: toHebrewApiErrorMessage(restoreError instanceof Error ? restoreError.message : 'שחזור השולחן נכשל'),
        autoClose: 3000,
      });
    } finally {
      restoreInFlightRef.current = false;
    }
  }, [restoreRequest, employeeSession, tenantId, setActiveTab]);

  return {
    tableHistoryTarget,
    restoreSourceOrder,
    restoreRequest,
    handleTableHistoryLongPress,
    handleSelectHistoryOrder,
    closeRestoreBill,
    openRestoreBillForOrderId,
    handleRestoreRequest,
    handleCancelRestorePick,
    handlePickRestoreTable,
  };
};
